import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  CaseSelectControlName,
  ColumnHeader,
  DocSignService,
  EgibObject,
  FormAlertService,
  MapAction,
  MapExtentUtils,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableActionType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  MapViewActionType,
  PortalId,
  PzService,
  ShapeUtils,
  StreetService,
  ToolType,
  disableValidators,
  markFormGroupsTouched,
  restoreValidators,
} from '@gk/gk-modules';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { MultiPersonDataComponent } from '../../../components/multi-person-data/multi-person-data.component';
import { ProxyDetailsComponent } from '../../../components/proxy-details/proxy-details.component';
import { RequestInProgress } from '../../../guards/request-in-progress/request-in-progress.guard';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import { FellingObjectsControlName } from '../../environmental-protection-portal/trees-and-shrubs-felling-request/services/new-request-form/new-request-form.model';
import { TASFNewRequestFormService } from '../../environmental-protection-portal/trees-and-shrubs-felling-request/services/new-request-form/new-request-form.service';
import {
  TSRNNewRequestControlName,
  TSRNNewRequestFormGroup,
} from '../services/new-request-form/new-request-form.model';
import { TSRNNewRequestFormService } from '../services/new-request-form/new-request-form.service';
import {
  TSRNNewRequest,
  TSRNNewRequestDto,
  TSRNNewRequestFormWorkspaceState,
  TSRNRequestMode,
} from '../services/new-request/new-request.model';
import { MainRoutes } from './../../../guards/guard.models';

@Component({
  selector: 'app-tsrn-new-request',
  templateUrl: './new-request.component.html',
  standalone: false,
})
export class TSRNNewRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy, RequestInProgress
{
  override controlName = TSRNNewRequestControlName;
  override formGroup: FormGroup<TSRNNewRequestFormGroup>;
  override portalId = PortalId.TreesAndShrubsRemovalNotification;
  readonly mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('mapObjectNumber', 'PARCEL_SEARCH_FORM.PARCEL_NUMBER'),
      new ColumnHeader('districtName', 'PARCEL_SEARCH_FORM.DISTRICT'),
      new ColumnHeader('mapSheet', 'PARCEL_SEARCH_FORM.SHEET'),
      new ColumnHeader('typeName', 'GK.MAP.TYPE', true),
      new ColumnHeader('area', 'GK.MAP.AREA_IN_HA'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    true,
    true,
    '25',
  );
  mapSettings: MapSettings;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  @ViewChild('ownerDetailsComponent')
  ownerDetailsComponent: MultiPersonDataComponent;
  @ViewChild('canDeactivateModal') canDeactivateModal: NgbModalRef;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  successSigned = false;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.TreesAndShrubsRemovalNotificationFormVersion;
  workspaceStateId = WorkspaceStateId.TreesAndShrubsRemovalNotificationForm;
  fellingObjectsControlName = FellingObjectsControlName;

  constructor(
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    private tsrnNewRequestFormService: TSRNNewRequestFormService,
    public override streetService: StreetService,
    public override toastrService: ToastrService,
    public override router: Router,
    private modalService: NgbModal,
    public override pzService: PzService,
    private tasfNewRequestFormService: TASFNewRequestFormService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
      undefined,
      toastrService,
    );
  }

  override ngOnInit(): void {
    this.fetchWrongFileText();
    this.subscribeToDocSignTranslations();
    this.createForm();
    this.fetchMapSettings();
    this.handleRequestModeChange();
  }

  createForm(): void {
    this.formGroup = this.tsrnNewRequestFormService.getFormGroup();
  }

  fetchMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.Applicant)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.mapSettings = mapSettings;
        this.setMapState();
      });
  }

  override setMapState(): void {
    this.mapState = new MapState(
      MapId.OwnershipConversionFeeCalculation,
      this.mapStateService.getViewState(
        MapId.OwnershipConversionFeeCalculation,
        this.mapSettings,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel, ToolType.AnyPolygon],
        this.mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.OwnershipConversionFeeCalculation,
        this.mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.isRequestDirty() && !this.successSigned
      ? from(
          this.modalService.open(this.canDeactivateModal, {
            beforeDismiss: () => false,
          }).result,
        )
      : true;
  }

  isRequestDirty(): boolean {
    return (
      this.formGroup.dirty || this.attachmentsComponent.areChosenDocuments()
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.attachmentsComponent.areDocumentsValid() &&
      (this.isNoObjectionCertificateRequestMode() || this.isMapSectionValid())
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getInvestorDetailsFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  sendRequest(): void {
    this.getRequestBody()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/interesant/wniosek/zgloszenieZamiaruUsunieciaKrzewuLubDrzewa/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: (error) => this.handleSendAndValidateFailure(error),
      });
  }

  getRequestBody = (): Observable<TSRNNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;
          this.toastrService.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        TSRNNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getLandParcels(),
          this.getIssueRangesMapObjects(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave = (): TSRNNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
    parcels: this.getLandParcels(),
    issueRanges: this.getIssueRangesMapObjects(),
  });

  handleLoadWorkspaceResponse(state: TSRNNewRequestFormWorkspaceState): void {
    if (state.formValue.shrubs) {
      state.formValue.shrubs.forEach(() => this.addShrubFormControl());
    }
    if (state.formValue.trees) {
      state.formValue.trees.forEach(() => this.addTreeFormControl());
    }
    this.formGroup.patchValue({
      ...state.formValue,
      investorDetails: {
        ...state.formValue.investorDetails,
        addressSelect: undefined,
      },
    });
    if (state.formValue?.investorDetails?.addressSelect) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
    }
    if (state.formValue?.proxyDetailsArray) {
      this.proxyDetailsComponent.setFormGroupValue(
        state.formValue.proxyDetailsArray,
      );
    }
    if (state.formValue?.owners) {
      this.ownerDetailsComponent.setFormGroupValue(state.formValue.owners);
    }
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    if (state.parcels) {
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddNew, [...state.parcels]),
      );
    }
    if (state.issueRanges) {
      const mapObjects = state.issueRanges.map((workRange) =>
        ShapeUtils.getAnyGeometryMapObject(
          workRange,
          MapObjectApiType.ExtentOrPolygon,
        ),
      );
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddToExisting, mapObjects),
      );
      this.handleMapAction(
        new MapAction(
          MapViewActionType.ExtentToFitToChange,
          MapExtentUtils.getMapExtentFromMapObjects(mapObjects),
        ),
      );
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.SelectRange, mapObjects),
      );
    }
  }

  getLandParcels(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  getIssueRangesMapObjects(): string[] {
    return (this.mapState.mapObjectTablesState[0].mapObjects || [])
      .filter(
        (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
      )
      .map((mapObject) => mapObject.geom);
  }

  getFormValue(): TSRNNewRequest {
    return this.formGroup.getRawValue();
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.TreesAndShrubsRemovalNotification}/requests-list`,
    );
  }

  override onRequestModeChange(requestMode: TSRNRequestMode): void {
    const newRemovalNotificationFormControls =
      this.getNewRemovalNotificationFormControls();
    const noObjectionCertificateRequestFormControls =
      this.getNoObjectionCertificateRequestFormControls();
    this.getNonBusinessPurposesFormControl().reset();
    this.getCaseFormControl().reset();
    switch (requestMode) {
      case TSRNRequestMode.NewRemovalNotification: {
        restoreValidators(newRemovalNotificationFormControls);
        disableValidators(noObjectionCertificateRequestFormControls);

        break;
      }
      case TSRNRequestMode.NoObjectionCertificateRequest: {
        restoreValidators(noObjectionCertificateRequestFormControls);
        disableValidators(newRemovalNotificationFormControls);

        break;
      }
      default: {
        console.warn(`Unknown request mode: ${requestMode}`);
      }
    }
  }

  getNewRemovalNotificationFormControls(): AbstractControl[] {
    return [this.getNonBusinessPurposesFormControl()];
  }

  getNoObjectionCertificateRequestFormControls(): AbstractControl[] {
    return [this.getCaseFormControl()];
  }

  getCaseFormControl(): AbstractControl {
    return this.formGroup.get(CaseSelectControlName.CaseUuid);
  }

  isNewRemovalNotificationMode(): boolean {
    return (
      this.getRequestModeValue() === TSRNRequestMode.NewRemovalNotification
    );
  }

  isNoObjectionCertificateRequestMode(): boolean {
    return (
      this.getRequestModeValue() ===
      TSRNRequestMode.NoObjectionCertificateRequest
    );
  }

  isMapSectionValid(): boolean {
    return this.areLandParcelsChosen() && this.areIssueRangesChosen();
  }

  areLandParcelsChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.LandParcel,
    );
  }

  areIssueRangesChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
    );
  }

  addTreeFormControl(): void {
    this.getTreesFormArray().push(
      this.tasfNewRequestFormService.getTreeFormGroup(),
    );
  }

  checkTreeFormGroupValidAndFormValidated(index: number): boolean {
    return !this.getTreeFormGroup(index).valid && this.submitted;
  }

  getTreeInventoryNumberFormControl(index: number): UntypedFormControl {
    return this.getTreeFormGroup(index).get(
      FellingObjectsControlName.InventoryNumber,
    ) as UntypedFormControl;
  }

  getTreeSpeciesNameFormControl(index: number): UntypedFormControl {
    return this.getTreeFormGroup(index).get(
      FellingObjectsControlName.SpeciesName,
    ) as UntypedFormControl;
  }

  getTreeTrunkCircumferenceFormControl(index: number): UntypedFormControl {
    return this.getTreeFormGroup(index).get(
      FellingObjectsControlName.TrunkCircumference,
    ) as UntypedFormControl;
  }

  getTreeFormGroup(index: number): UntypedFormGroup {
    return this.getTreesFormArray().controls[index] as UntypedFormGroup;
  }

  deleteTreeFormControl(index: number): void {
    this.getTreesFormArray().removeAt(index);
  }

  areSomeTreesOrShrubsFormGroups(): boolean {
    return this.areSomeTreesFormGroups() || this.areSomeShrubsFormGroups();
  }

  areSomeTreesFormGroups(): boolean {
    return !!this.getTreesFormArray().controls.length;
  }

  areSomeShrubsFormGroups(): boolean {
    return !!this.getShrubsFormArray().controls.length;
  }

  getTreesFormArray(): UntypedFormArray {
    return this.formGroup.get(
      TSRNNewRequestControlName.Trees,
    ) as UntypedFormArray;
  }

  checkShrubFormGroupValidAndFormValidated(index: number): boolean {
    return !this.getShrubFormGroup(index).valid && this.submitted;
  }

  getShrubInventoryNumberFormControl(index: number): UntypedFormControl {
    return this.getShrubFormGroup(index).get(
      FellingObjectsControlName.InventoryNumber,
    ) as UntypedFormControl;
  }

  getShrubSpeciesNameFormControl(index: number): UntypedFormControl {
    return this.getShrubFormGroup(index).get(
      FellingObjectsControlName.SpeciesName,
    ) as UntypedFormControl;
  }

  getShrubProjectionSurfaceFormControl(index: number): UntypedFormControl {
    return this.getShrubFormGroup(index).get(
      FellingObjectsControlName.ProjectionSurface,
    ) as UntypedFormControl;
  }

  getShrubFormGroup(index: number): UntypedFormGroup {
    return this.getShrubsFormArray().controls[index] as UntypedFormGroup;
  }

  deleteShrubFormControl(index: number): void {
    this.getShrubsFormArray().removeAt(index);
  }

  addShrubFormControl(): void {
    this.getShrubsFormArray().push(
      this.tasfNewRequestFormService.getShrubFormGroup(),
    );
  }

  getShrubsFormArray(): UntypedFormArray {
    return this.formGroup.get(
      TSRNNewRequestControlName.Shrubs,
    ) as UntypedFormArray;
  }

  isNonBusinessPurposesFormControlValid(): boolean {
    return this.getNonBusinessPurposesFormControl().valid;
  }

  getNonBusinessPurposesFormControl(): UntypedFormControl {
    return this.formGroup.get(
      TSRNNewRequestControlName.NonBusinessPurposes,
    ) as UntypedFormControl;
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
